import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { ReferralFormFields, ReferralFormInputs, ReferralsQueryKeys, useEditReferralsMutation, useGetByIdReferralsQuery } from '@/modules/Sites';

import { ReferralsFormView } from './ReferralsFormView';
import { referralFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  referralId: string;
}

export const ReferralsEditFormContainer: FC<Props> = memo(({ siteId, referralId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ReferralFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof ReferralFormInputs>>(null);

  const {
    data: referralData,
    error: referralError,
    isLoading: isReferralLoading,
    isError: isReferralError,
    refetch: refetchReferral
  } = useGetByIdReferralsQuery(siteId, referralId)({
    enabled: !!referralId && !!siteId
  });

  const { mutate: editReferralMutation, isLoading: editReferralsIsLoading } =
  useEditReferralsMutation(siteId, referralId)({
    onSuccess: async () => {
      await queryClient.refetchQueries([ReferralsQueryKeys.GetAll, siteId]);

      await refetchReferral();

      appMessageApi.success('Реферал успішно змінено!');

      navigate(AppRoutes.ReferralsById.replace(':siteId', siteId).replace(':referralId', referralId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof ReferralFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ReferralFormFields = {
    name: referralData?.name,
    slug: referralData?.slug,
    siteId
  };

  const handleFinish = useCallback((values: ReferralFormFields): void => {
    setFormErrors(null);
    editReferralMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.ReferralsById.replace(':siteId', siteId).replace(':referralId', referralId));
  }, [siteId, referralId]);

  return (
    <FallbackedLayout
      error={ referralError }
      isError={ isReferralError }
      isLoading={ isReferralLoading }
    >
      {
        () => (
          <ReferralsFormView
            formName="referral-edit"
            formType="edit"
            form={ form }
            formErrors={ formErrors }
            initialValues={ initialValues }
            isFormLoading={ editReferralsIsLoading }
            validationSchema={ referralFormValidationSchema }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
